import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import MCQLogic from "../../SubtopicsPage/MCQLogic";
import { CodeBlock } from "../../CodeOutputBlocks";

const questionsData = [
  {
    question: (
      <div>
        <p>What is the output?</p>
        <CodeBlock
          language="python"
          code={`for i in range(6):\n    if i == 4:\n        break\n    print(i)`}
        />
      </div>
    ),
    options: ["0\n1\n2\n3\n4", "0\n1\n2\n3", "4", "0\n1\n2\n3\n4\n5"],
    answer: "0\n1\n2\n3",
  },

  {
    question: (
      <div>
        <p>What numbers are printed?</p>
        <CodeBlock
          language="python"
          code={`i = 0\nwhile i < 5:\n    if i == 3:\n        break\n    print(i)\n    i += 1`}
        />
      </div>
    ),
    options: ["0\n1\n2", "0\n1\n2\n3", "3", "No output"],
    answer: "0\n1\n2",
  },

  {
    question: (
      <div>
        <p>What is the output?</p>
        <CodeBlock
          language="python"
          code={`for i in range(5):\n    if i == 2:\n        continue\n    print(i)`}
        />
      </div>
    ),
    options: ["0\n1\n3\n4", "0\n1\n2\n3\n4", "2", "0\n1\n2"],
    answer: "0\n1\n3\n4",
  },

  {
    question: (
      <div>
        <p>What is printed?</p>
        <CodeBlock
          language="python"
          code={`for i in range(1, 6):\n    if i % 2 == 0:\n        continue\n    print(i)`}
        />
      </div>
    ),
    options: ["1\n3\n5", "2\n4", "1\n2\n3\n4\n5", "No output"],
    answer: "1\n3\n5",
  },

  {
    question: (
      <div>
        <p>What is the output?</p>
        <CodeBlock
          language="python"
          code={`for i in range(3):\n    for j in range(3):\n        if j == 1:\n            break\n        print("i=", i, "j=", j)`}
        />
      </div>
    ),
    options: [
      "i= 0 j= 0\ni= 1 j= 0\ni= 2 j= 0",
      "i= 0 j= 0\ni= 0 j= 1 only",
      "All 9 pairs",
      "Error",
    ],
    answer: "i= 0 j= 0\ni= 1 j= 0\ni= 2 j= 0",
  },

  {
    question: (
      <div>
        <p>What is the output?</p>
        <CodeBlock
          language="python"
          code={`for i in range(4):\n    if i == 2:\n        pass\n    else:\n        print(i)`}
        />
      </div>
    ),
    options: ["0\n1\n3", "0\n1\n2\n3", "0\n1\n3\n2", "No output"],
    answer: "0\n1\n3",
  },

  {
    question: (
      <div>
        <p>What happens when this runs?</p>
        <CodeBlock
          language="python"
          code={`i = 0\nwhile i < 3:\n    i += 1\n    if i == 2:\n        continue\n    print(i)`}
        />
      </div>
    ),
    options: ["1\n3", "1\n2\n3", "2", "Infinite loop"],
    answer: "1\n3",
  },

  {
    question: (
      <div>
        <p>What is the final output?</p>
        <CodeBlock
          language="python"
          code={`for i in range(10):\n    if i == 5:\n        break\n    if i % 2 == 0:\n        continue\n    print(i)`}
        />
      </div>
    ),
    options: ["1\n3", "0\n1\n2\n3\n4", "1\n3\n5", "No output"],
    answer: "1\n3",
  },

  {
    question: (
      <div>
        <p>What does this code print?</p>
        <CodeBlock
          language="python"
          code={`for i in range(3):\n    pass\nprint("Finished")`}
        />
      </div>
    ),
    options: ["Nothing", "Finished", "0 1 2", "Error"],
    answer: "Finished",
  },

  {
    question: (
      <div>
        <p>How many numbers are printed?</p>
        <CodeBlock
          language="python"
          code={`count = 0\nfor i in range(100):\n    if i == 10:\n        break\n    count += 1\nprint(count)`}
        />
      </div>
    ),
    options: ["100", "10", "11", "0"],
    answer: "10",
  },

  // ================= NORMAL QUESTIONS =================

  {
    question: "What happens when break is executed inside a loop?",
    options: [
      "Loop skips one iteration",
      "Loop terminates immediately",
      "Loop restarts from beginning",
      "Program crashes",
    ],
    answer: "Loop terminates immediately",
  },

  {
    question: "What does continue do in a loop?",
    options: [
      "Exits the entire loop",
      "Skips the rest of the current iteration and moves to next",
      "Does nothing",
      "Prints a message",
    ],
    answer: "Skips the rest of the current iteration and moves to next",
  },

  {
    question: "When is pass statement commonly used?",
    options: [
      "To exit loop",
      "As a placeholder when code is not ready",
      "To skip iteration",
      "To print nothing",
    ],
    answer: "As a placeholder when code is not ready",
  },

  {
    question: "In a nested loop, break in the inner loop affects:",
    options: [
      "Only the inner loop",
      "Both inner and outer loops",
      "Only the outer loop",
      "The entire program",
    ],
    answer: "Only the inner loop",
  },

  {
    question: "Which statement does absolutely nothing when executed?",
    options: ["break", "continue", "pass", "return"],
    answer: "pass",
  },
];

const LoopControlStmts_MCQ = ({
  subtopicId,
  goalName,
  courseName,
  onComplete,
}) => {
  const { markSubtopicComplete, loadProgressSummary, completedContent } =
    useAuth();

  const [isCompleted, setIsCompleted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const randomQuestions = [...questionsData].sort(() => Math.random() - 0.5);

  useEffect(() => {
    if (subtopicId && completedContent.includes(subtopicId)) {
      setIsCompleted(true);
    }
  }, [completedContent, subtopicId]);

  const handleCompletion = async () => {
    if (isLoading || isCompleted) return;

    try {
      setIsLoading(true);
      const result = await markSubtopicComplete(
        subtopicId,
        goalName || "Goal 1",
        courseName || "Static Website: HTML CSS & Bootstrap"
      );

      if (result.success) {
        await loadProgressSummary();
        setIsCompleted(true);
        if (onComplete) onComplete();
      } else {
        alert(result.message);
      }
    } catch (error) {
      alert("Failed to mark as complete");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MCQLogic
      title="Loop Control Statements | MCQs"
      questions={randomQuestions}
      isCompleted={isCompleted}
      isLoading={isLoading}
      onComplete={handleCompletion}
      subtopicId={subtopicId}
      goalName={goalName}
      courseName={courseName}
    />
  );
};

export default LoopControlStmts_MCQ;
